// src/pages/ItemsPage.js

import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import './ItemsPage.css';

export default function ItemsPage() {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const preRef = useRef(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const resp = await api.get('/items');
      setItems(resp.data);
    } catch (err) {
      console.error('Error fetching items:', err);
      alert('Failed to load items.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('name', name);
    formData.append('price', price);
    if (imageFile) formData.append('image', imageFile);

    try {
      await api.post('/items', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setName('');
      setPrice('');
      setImageFile(null);
      fetchItems();
    } catch (err) {
      console.error('Error adding item:', err);
      const msg = err.response?.data?.error || err.message || 'Unknown error';
      alert(`Failed to add item:\n${msg}`);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api.delete(`/items/${itemId}`);
      fetchItems();
    } catch (err) {
      console.error('Error deleting item:', err);
      const msg = err.response?.data?.error || err.message || 'Unknown error';
      alert(`Failed to delete item:\n${msg}`);
    }
  };

  const handleEdit = async (item) => {
    const newName = prompt('Enter new name:', item.Name);
    if (newName == null) return;
    const newPriceStr = prompt(
      'Enter new price:',
      item.Price != null ? parseFloat(item.Price).toFixed(2) : '0.00'
    );
    if (newPriceStr == null) return;

    const newPrice = parseFloat(newPriceStr);
    if (isNaN(newPrice) || newPrice <= 0) {
      alert('Invalid price.');
      return;
    }

    const replaceImage = window.confirm(
      'Replace the image? Click OK to select a new file, Cancel to keep current.'
    );
    let updatedImageFile = null;
    if (replaceImage) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async () => {
        const file = input.files[0];
        if (!file) return;
        updatedImageFile = file;
        await sendEditRequest(item.ItemID, newName, newPrice, updatedImageFile);
      };
      input.click();
    } else {
      await sendEditRequest(item.ItemID, newName, newPrice, null);
    }
  };

  const sendEditRequest = async (itemId, newName, newPrice, imageFile) => {
    try {
      if (imageFile) {
        const formData = new FormData();
        formData.append('name', newName);
        formData.append('price', newPrice);
        formData.append('image', imageFile);
        await api.put(`/items/${itemId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.put(`/items/${itemId}`, {
          name: newName,
          price: newPrice,
        });
      }
      fetchItems();
    } catch (err) {
      console.error('Error updating item:', err);
      const msg = err.response?.data?.error || err.message || 'Unknown error';
      alert(`Failed to update item:\n${msg}`);
    }
  };

  // Filter for on-screen grid
  const filteredItems = items.filter((item) => {
    const lower = searchTerm.toLowerCase();
    const nameMatch = item.Name.toLowerCase().includes(lower);
    const priceNum = parseFloat(item.Price);
    const priceMatch = !isNaN(priceNum)
      ? priceNum.toFixed(2).includes(lower)
      : false;
    return nameMatch || priceMatch;
  });

  // Helpers for fixed-width lines (32 chars total)
  const padName = (s) => {
    const maxLen = 20;
    if (s.length > maxLen) return s.slice(0, maxLen - 3) + '...';
    return s.padEnd(maxLen, ' ');
  };
  const padPrice = (p) => {
    const num = parseFloat(p);
    const str = `₹${isNaN(num) ? '0.00' : num.toFixed(2)}`;
    // 8 chars for price column, right-aligned
    return str.padStart(8, ' ');
  };

  // Build <pre> text
  const nowStr = new Date().toLocaleString();
  const headerLine = `${padName('Item Name')}    ${'    Price'.padStart(8, ' ')}`;
  let rows = [headerLine, '-'.repeat(32)];
  items.forEach((item) => {
    const lineName = padName(item.Name);
    const linePrice = padPrice(item.Price);
    rows.push(`${lineName}    ${linePrice}`);
  });
  rows.push('-'.repeat(32));
  const totalLine = `Total Items: ${items.length.toString().padStart(2, ' ')}`.padEnd(32, ' ');
  rows.push(totalLine);
  const preContent = rows.join('\n');

  const handlePrint = () => {
    if (!preRef.current) return;

    const inlineStyle = `
      <style>
        @page {
          size: 2in auto;
          margin: 0;
        }
        body {
          margin: 0;
          padding: 4px;
          width: 460px; /* ~58 mm at 203 dpi */
          font-family: "Courier New", Courier, monospace;
          font-size: 12px; /* increased from 10px */
          line-height: 1.2;
          color: #000;
        }
        .header {
          text-align: center;
          font-size: 14px; /* slightly larger */
          margin-bottom: 4px;
          white-space: nowrap;
        }
        .title {
          text-align: center;
          font-size: 14px;
          margin: 4px 0;
          white-space: nowrap;
        }
        pre {
          white-space: pre;
          font-family: monospace;
          margin: 0;
        }
        .footer {
          text-align: center;
          font-size: 10px;
          margin-top: 6px;
        }
      </style>
    `;

    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8"/>
          <title>Print Items</title>
          ${inlineStyle}
        </head>
        <body>
          <div class="header">${nowStr}</div>
          <div class="title">Item List</div>
          <pre>${preContent}</pre>
          <div class="footer">All right reserved</div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 300);
            };
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) {
      alert('Please allow popups for printing.');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(printHTML);
    printWindow.document.close();
  };

  return (
    <div className="page-container">
      <button className="btn-back" onClick={() => navigate(-1)}>
        ← Back
      </button>

      <h2>Items</h2>

      <form onSubmit={handleSubmit} className="form add-item-form">
        <input
          type="text"
          placeholder="Item name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <input
          type="number"
          step="0.01"
          placeholder="Price"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
        />

        <input
          type="file"
          accept="image/*"
          onChange={(e) => setImageFile(e.target.files[0])}
        />

        <button type="submit">Add Item</button>
      </form>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search by name or price..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <button className="btn-print" onClick={handlePrint}>
          Print Items
        </button>
      </div>

      <h3 className="section-title">Existing Items</h3>
      <div className="item-grid">
        {filteredItems.map((item) => (
          <div key={item.ItemID} className="item-card">
            {item.ImageUrl ? (
              <img
                src={`${api.defaults.baseURL.replace(/\/api$/, '')}${item.ImageUrl}`}
                alt={item.Name}
                className="item-image"
              />
            ) : (
              <div className="no-image">No Image</div>
            )}
            <h4 className="item-name">{item.Name}</h4>
            <p className="item-price">₹{parseFloat(item.Price).toFixed(2)}</p>
            <div className="item-buttons">
              <button className="btn-edit" onClick={() => handleEdit(item)}>
                Edit
              </button>
              <button
                className="btn-delete"
                onClick={() => handleDelete(item.ItemID)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {filteredItems.length === 0 && (
          <p className="no-results">No items match your search.</p>
        )}
      </div>

      {/* Hidden <pre> for on-screen preview */}
      <div className="items-preview-container">
        <div className="header">{nowStr}</div>
        <div className="title">Item List</div>
        <pre ref={preRef} className="preformatted">
          {preContent}
        </pre>
        <div className="footer">Powered by VizTech Billing App</div>
      </div>
    </div>
  );
}
